import React, { useEffect, useState } from 'react'

import { ThemeProvider } from '@mui/material'

import { getTheme } from 'themes'
import { useTranslation } from 'language/translator'

/* Atoms */
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import { IconProps } from 'components_new/atoms/Icon'
import Link from 'components_new/atoms/Link'
import Text from 'components_new/atoms/Text'

/* Molecules */
import CheckboxItem from 'components_new/molecules/CheckboxItem'
import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import GenericAvatar from 'components_new/molecules/GenericAvatar'
import LoadingSection from 'components_new/molecules/LoadingSection'
import RadioItem from 'components_new/molecules/RadioItem'
import SearchField, {
  SearchFieldProps
} from 'components_new/molecules/SearchField'
import { Option } from 'components_new/molecules/SelectSearch'
import SwitchItem from 'components_new/molecules/SwitchItem'
import VirtualizedList from 'components_new/molecules/VirtualizedList'

type SearchAndSelectBaseProps = Omit<
  SearchFieldProps,
  'onClear' | 'onSearch' | 'value'
> & {
  endIcon?: IconProps
  loading?: boolean
  /** Allow clearing selection (default: true) */
  nullable?: boolean
  options: Option[]
}

type MultipleProps = SearchAndSelectBaseProps & {
  multiple?: true
  onChange?: (value: string[]) => void
  onSubmit?: (value: string[]) => void
  value: string[]
}

type SingleProps = SearchAndSelectBaseProps & {
  multiple: false
  onChange?: (value: string | null) => void
  onSubmit?: (value: string | null) => void
  value: string | null
}

export type SearchAndSelectProps = MultipleProps | SingleProps

const toArray = (props: SearchAndSelectProps): string[] => {
  if (props.multiple === false) {
    return props.value != null ? [props.value] : []
  }

  return props.value
}

const compareValueAndTempSelection = (
  value: string[],
  tempSelection: string[]
) => {
  if (value.length !== tempSelection.length) {
    return false
  }

  const sortedValue = [...value].sort().join(',')
  const sortedTempSelection = [...tempSelection].sort().join(',')

  return sortedValue === sortedTempSelection
}

const SearchAndSelect = (props: SearchAndSelectProps) => {
  const { loading, nullable = true, options, sx } = props

  // Destructure rest for SearchField (cast to base to get clean spread)
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const {
    endIcon,
    multiple: _multiple,
    onChange: _onChange,
    onSubmit: _onSubmit,
    options: _options,
    sx: _sx,
    value: _value,
    ...rest
  } = props as MultipleProps
  /* eslint-enable @typescript-eslint/no-unused-vars */

  const isMultiple = props.multiple !== false

  const { t } = useTranslation()

  const [searchQuery, setSearchQuery] = useState('')
  const [tempSelection, setTempSelection] = useState<string[]>(toArray(props))
  const [showTempSelection, setShowTempSelection] = useState<boolean>(false)

  // sync internal state with value prop
  useEffect(() => {
    handleSetTempSelection(toArray(props))
  }, [props.value])

  const filteredOptions = options
    .filter((option) => {
      if (showTempSelection) {
        return tempSelection.includes(option.id as string)
      }

      return true
    })
    .filter((option) =>
      option.title.toLowerCase().includes(searchQuery.toLowerCase())
    )

  const handleSetTempSelection = (selection: string[]) => {
    setTempSelection(selection)

    if (selection.length === 0) {
      setShowTempSelection(false)
    }
  }

  const fireOnChange = (selection: string[]) => {
    if (props.multiple === false) {
      props.onChange?.(selection[0] ?? null)
    } else {
      props.onChange?.(selection)
    }
  }

  const fireOnSubmit = (selection: string[]) => {
    if (props.multiple === false) {
      props.onSubmit?.(selection[0] ?? null)
    } else {
      props.onSubmit?.(selection)
    }
  }

  const handleOptionToggle = (newId: string) => {
    let newSelection: string[]

    if (isMultiple) {
      // Multi-select: toggle the option
      newSelection = tempSelection.includes(newId)
        ? tempSelection.filter((id) => id !== newId)
        : [...tempSelection, newId]
    } else {
      // Single-select: select new or deselect if already selected
      if (tempSelection.includes(newId)) {
        // Don't deselect if nullable is false
        newSelection = nullable ? [] : [newId]
      } else {
        newSelection = [newId]
      }
    }

    handleSetTempSelection(newSelection)
    fireOnChange(newSelection)
  }

  const handleClearAll = () => {
    handleSetTempSelection([])
    fireOnChange([])
  }

  const handleSelectAll = () => {
    const filteredOptionIds = filteredOptions.map(
      (option) => option.id as string
    )

    // Merge with the existing selection so options hidden by the current
    // search query keep their selected state instead of being dropped.
    const newSelection = Array.from(
      new Set([...tempSelection, ...filteredOptionIds])
    )

    handleSetTempSelection(newSelection)
    fireOnChange(newSelection)
  }

  const valueAsArray = toArray(props)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', ...sx }}>
      <SearchField
        disabled={loading}
        onClear={() => setSearchQuery('')}
        onSearch={(value) => setSearchQuery(value)}
        sx={{ flex: 0, mb: 2 }}
        value={searchQuery}
        {...rest}
      />
      {isMultiple ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <Text color={loading ? 'text.disabled' : undefined}>
            <b>{loading ? 0 : tempSelection.length}</b>{' '}
            {t('global', 'selected', true)}
          </Text>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1
            }}
          >
            {nullable ? (
              <Link
                disabled={loading || tempSelection.length === 0}
                onClick={handleClearAll}
              >
                {t('global', 'clear_all')}
              </Link>
            ) : null}
            <Link
              disabled={
                loading ||
                filteredOptions.every((option) =>
                  tempSelection.includes(option.id as string)
                )
              }
              onClick={handleSelectAll}
            >
              {t('global', 'select_all')}
            </Link>
          </Box>
        </Box>
      ) : null}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          height: 256,
          mb: isMultiple ? 1 : undefined
        }}
      >
        {loading ? (
          <LoadingSection sx={{ p: 2 }} />
        ) : (
          <>
            {!isMultiple && nullable ? (
              <RadioItem
                checked={tempSelection.length === 0}
                fullWidth={true}
                hover={true}
                label={t('global', 'none')}
                onChange={() => {
                  handleSetTempSelection([])
                  fireOnChange([])
                }}
                size="small"
                TextProps={{
                  color:
                    tempSelection.length !== 0
                      ? 'text.secondary'
                      : 'text.primary'
                }}
              />
            ) : null}

            {filteredOptions.length === 0 ? (
              <EmptyPlaceholder title={t('global', 'no_options_found')} />
            ) : (
              <VirtualizedList
                data={filteredOptions}
                computeItemKey={(_index, option) => option.id as string}
                itemContent={(_index, option) => {
                  const checked = tempSelection.includes(option.id as string)

                  const label = (
                    <Box
                      component="span"
                      sx={{ alignItems: 'center', display: 'flex', gap: 1 }}
                    >
                      {option.avatar ? (
                        <GenericAvatar
                          body={option.body}
                          color={option.color}
                          size="small"
                          title={option.title}
                          variant={option.avatar}
                        />
                      ) : null}
                      {option.title}
                    </Box>
                  )

                  return isMultiple ? (
                    <CheckboxItem
                      checked={checked}
                      endIcon={endIcon}
                      fullWidth={true}
                      hover={true}
                      label={label}
                      onChange={() => handleOptionToggle(option.id as string)}
                      size="small"
                      TextProps={{
                        color: !checked ? 'text.secondary' : 'text.primary',
                        ['&:hover']: {
                          color: 'text.primary'
                        }
                      }}
                    />
                  ) : (
                    <RadioItem
                      checked={checked}
                      endIcon={endIcon}
                      fullWidth={true}
                      hover={true}
                      label={label}
                      onChange={() => handleOptionToggle(option.id as string)}
                      size="small"
                      TextProps={{
                        color: !checked ? 'text.secondary' : 'text.primary'
                      }}
                    />
                  )
                }}
              />
            )}
          </>
        )}

        {props.onSubmit ? (
          <Collapse
            in={!compareValueAndTempSelection(valueAsArray, tempSelection)}
          >
            <ThemeProvider theme={getTheme('dark')}>
              <Box
                sx={{
                  alignItems: 'center',
                  bgcolor: 'background.paper',
                  display: 'flex',
                  justifyContent: 'space-between',
                  px: 1,
                  py: 0.5
                }}
              >
                <Text variant="button">{t('global', 'unsaved_changes')}</Text>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    onClick={() => setTempSelection(valueAsArray)}
                    size="small"
                    variant="text"
                  >
                    {t('global', 'cancel')}
                  </Button>
                  <Button
                    onClick={() => fireOnSubmit(tempSelection)}
                    size="small"
                  >
                    {t('global', 'save')}
                  </Button>
                </Box>
              </Box>
            </ThemeProvider>
          </Collapse>
        ) : null}
      </Box>

      {isMultiple ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <SwitchItem
            checked={showTempSelection}
            disabled={loading || tempSelection.length === 0}
            label={
              <>
                {t('global', 'show_selected')}{' '}
                <b>{`(${tempSelection.length})`}</b>
              </>
            }
            onChange={() => setShowTempSelection(!showTempSelection)}
            size="small"
            TextProps={{
              color: !showTempSelection ? 'text.secondary' : undefined
            }}
          />
        </Box>
      ) : null}
    </Box>
  )
}

export default SearchAndSelect
